import { useMemo, useState } from 'react';
import { addDays, eachDayOfInterval, format, isFirstDayOfMonth, isSameDay } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { Box, Carousel, Gleam, IconButton, Paragraph, Text, ToggleCard } from '@zillow/constellation-10';
import { TourStatusForZcheduler, type TourWebApiDto } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getToursForListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import BookedToursSection from './components/BookedToursSection';
import { convertDateToLocalDate, formatTimeString } from '../../utils/formatTime';

import { LoadingHeading } from '@zg-rentals/loading-components';

import type { Tour } from '../../constants/UpcomingToursConstants';
import { IconChevronLeftFilled, IconChevronRightFilled } from '@zillow/constellation-icons';
import { AVAILABLE_RESCHEDULE_DATE_KEY_FORMAT } from '../../constants';

type UpcomingToursProps = {
  inquiriesPath: string;
  isScreenMobile: boolean;
  listingAlias: string;
  onCalendarClick: () => void;
  tourCalendarPath: string;
  eventId?: string;
};

const UpcomingTours = ({
  inquiriesPath,
  isScreenMobile,
  listingAlias,
  onCalendarClick,
  tourCalendarPath,
  eventId,
}: UpcomingToursProps) => {
  const today = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(today);

  const currentDates = useMemo(() => {
    const end = addDays(today, 13);
    return eachDayOfInterval({ start: today, end });
  }, [today]);

  const formatDateTourKey = (date: Date) => format(date, AVAILABLE_RESCHEDULE_DATE_KEY_FORMAT);

  const formatTourTime = (tour: TourWebApiDto) => {
    const startDate = new TZDate(tour.startMs, tour.zoneId);
    const endDate = new TZDate(tour.endMs, tour.zoneId);

    const startTime = formatTimeString(startDate, { truncateZeroMinutes: true });
    const endTime = formatTimeString(endDate, { showMeridiem: true });

    return `${startTime}-${endTime}`;
  };

  const { data, isLoading } = useZCalendarWebApiHandler_getToursForListing({
    listingAlias,
    bookedByBrand: '',
    bookedByUserId: '',
    fromDate: convertDateToLocalDate(currentDates[0]),
    toDate: convertDateToLocalDate(currentDates[currentDates.length - 1]),
    tourStatuses: [TourStatusForZcheduler.CONFIRMED],
  });

  const toursByDate = useMemo(
    () =>
      data?.tours.reduce<Record<string, Array<Tour>>>((acc, tour) => {
        const date = new Date(tour.startMs);

        const key = formatDateTourKey(date);
        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push({
          conversationId: tour.conversationId,
          tourId: tour.id,
          tourName: tour.bookedByName,
          tourTime: formatTourTime(tour),
        });

        return acc;
      }, {}) ?? {},
    [data],
  );

  const getToursForDate = (date: Date) => toursByDate[formatDateTourKey(date)] ?? [];

  const selectedDateTours = getToursForDate(selectedDate);

  const carouselHeading = (
    <LoadingHeading isLoading={isLoading} loadingWidth="100%" level={3}>
      <Text
        className="ConnectedCard-Heading"
        asChild
        css={{
          textStyle: { base: 'body-bold', lg: 'heading-xs' },
          marginRight: { base: 'tighter', lg: 'default' },
        }}
      >
        <h2>Upcoming tours</h2>
      </Text>
    </LoadingHeading>
  );

  const formatDate = (date: Date) => {
    const formatPattern = isScreenMobile ? 'eeeee' : 'eee';
    return format(date, formatPattern).toUpperCase();
  };

  return (
    <>
      <Carousel
        heading={carouselHeading}
        slideGap="xs"
        renderNavigationControls={(leftButtonProps, rightButtonProps) => {
          const { ref: _leftButtonRef, ...leftButtonPropsWithoutRef } = leftButtonProps;
          const { ref: _rightButtonRef, ...rightButtonPropsWithoutRef } = rightButtonProps;
          return (
            <Carousel.NavControls css={{ gap: 'default' }}>
              <IconButton
                shape="circle"
                aria-label="Previous dates"
                icon={<IconChevronLeftFilled />}
                size="sm"
                tabIndex={-1}
                title="Previous dates"
                {...leftButtonPropsWithoutRef}
              />
              <IconButton
                shape="circle"
                aria-label="Next dates"
                icon={<IconChevronRightFilled />}
                size="sm"
                tabIndex={-1}
                title="Next dates"
                {...rightButtonPropsWithoutRef}
              />
            </Carousel.NavControls>
          );
        }}
      >
        {currentDates.map((date: Date, index: number) => {
          const hasMonthLabel = index === 0 || isFirstDayOfMonth(date);
          return (
            <Box
              key={index}
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Paragraph
                textStyle="body-sm"
                css={{
                  display: 'inline-block',
                  height: '24px',
                  verticalAlign: 'top',
                  width: '25px',
                  marginBottom: 'tighter',
                }}
              >
                {hasMonthLabel && format(date, 'LLL')}
              </Paragraph>
              <ToggleCard
                outlined
                elevated={false}
                tone="neutral"
                selected={isSameDay(date, selectedDate)}
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingBlock: 'tighter',
                  paddingInline: 'tight',
                  width: { base: '40px', md: '52px' },
                }}
                onClick={() => setSelectedDate(date)}
                data-testid={`UpcomingTours-date-${index}`}
              >
                <Text textStyle="fineprint">{formatDate(date)}</Text>
                <Text textStyle="body-bold">{format(date, 'd')}</Text>
                {getToursForDate(date)?.length > 0 ? (
                  <Gleam
                    appearance="dot"
                    count={1}
                    dotProps={{
                      label: `New tours for ${formatDateTourKey(date)}`,
                      css: { height: '{sizes.indicator.sm}', width: '{sizes.indicator.sm}' },
                    }}
                    css={{ height: '{sizes.indicator.sm}', width: '{sizes.indicator.sm}' }}
                    tone="default"
                  />
                ) : (
                  <Box css={{ height: '6px' }} />
                )}
              </ToggleCard>
            </Box>
          );
        })}
      </Carousel>

      <BookedToursSection
        inquiriesPath={inquiriesPath}
        onCalendarClick={onCalendarClick}
        tours={selectedDateTours}
        tourCalendarPath={tourCalendarPath}
        isLoading={isLoading}
        eventId={eventId}
      />
    </>
  );
};

export default UpcomingTours;
